import { Directive, EventEmitter, Input, OnDestroy } from '@angular/core';
import { WorkflowItemStatus } from '@api/projects/interfaces';
import { CustomStateTransition, IWorkflowItem } from '@interfaces/interfaces';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { BehaviorSubject, combineLatest, concat, EMPTY, Observable, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, scan, shareReplay, skip, startWith, switchMap, take, tap } from 'rxjs/operators';
import { WorkflowRestService } from 'src/app/services/rest/workflow-rest.service';
import { getAppStateSettings } from 'src/app/store/reducers';
import { SessionItemData } from 'src/app/workflow/services/session-item-data';
import { WorkflowService } from '../../services/workflow.service';

export enum CommunicationStatus {
	UNKNOWN,
	PENDING,
	DONE,
}

let instance_id_counter = 0;

@Directive({
	selector: '[appWorkflowItem]'
})
export class WorkflowItemDirective implements OnDestroy {

	/**
	 * Current active IWorkflowItem
	 */
	@Input('appWorkflowItem') 
	public set workflowItem(item: IWorkflowItem){
		this.workflowItem$.next(item);
	}

	/**
	 * Observable with active IWorkflowItem
	 */
	readonly workflowItem$: BehaviorSubject<IWorkflowItem> = new BehaviorSubject(undefined);

	/**
	 * Observable for the current workflow items SessionItemData
	 */
	readonly item$: BehaviorSubject<SessionItemData> = new BehaviorSubject(undefined);

	/**
	 * Observable for the completed workflow items SessionItemData
	 */
	readonly itemFull$: BehaviorSubject<SessionItemData> = new BehaviorSubject(undefined);

	/**
	 * Observable for the completed or current SessionItemData
	 */
	readonly current$: Observable<SessionItemData>;

	/**
	 * WorkflowItemStatus
	 */
	readonly workflowStatus$: Observable<WorkflowItemStatus>;

	private update$ = new EventEmitter<Record<string, string>>();
	private updateStatus$ = new EventEmitter<Partial<WorkflowItemStatus>>();
	private _status: BehaviorSubject<CommunicationStatus> = new BehaviorSubject(null);
	private _projectId: string;
	private _participantId: string;
	private _itemId: string;
	private _item = new SessionItemData();
	private _itemFull = new SessionItemData();
	private subscription = new Subscription();

	// Instanz-Id zum Tracking, ob versehentlich mehrere Instanzen angelegt wurden
	readonly instance_id = instance_id_counter++;

	constructor(
		private store: Store,
		private workflowRestService: WorkflowRestService,
		private workflowService: WorkflowService
	) {
		
		const state$ = this.store.select(getAppStateSettings).pipe(
			map(state => ({ projectId: state.activeProjectID, participantId: state.activeParticipantID })),
			switchMap(state => this.workflowItem$.pipe(
				map(workflowItem => ({ ...state, itemId: workflowItem?.id })),
			)),
			tap(state => {
				this._projectId = state.projectId;
				this._participantId = state.participantId;
				this._itemId = state.itemId;
			}),
			filter(state => !!state.projectId && !!state.participantId && !!state.itemId),
			shareReplay({refCount: true, bufferSize: 1}),
		);

		const item$ = state$.pipe(
			switchMap(state => {
				return concat(
					// Aktuellen Status abrufen
					this.workflowRestService.getWorkflowItemStatus$(state.projectId, state.participantId, state.itemId),
					// Updates durchführen und Antworten verarbeiten
					this.update$.pipe(
						// Update duchführen
						switchMap(data => this.workflowRestService.updateStatus$(this._projectId, this._participantId, this._itemId, {data: data})),
						// Fehler im Update dürfen die Pipe nicht beenden
						catchError((error: unknown) => {
							console.error(error);
							return EMPTY;
						}),
						// Das Update kann den customState verändern. Dieses Update weiterreichen
						tap(data => this.updateStatus$.next({ customState: data.customState, started: true })),
						tap(() => workflowService.changed()),
					),
				);
			}),
			catchError((error: unknown) => {
				console.error(error);
				return EMPTY;
			}),
			map(workflowItemData => new SessionItemData(workflowItemData.data)),
			tap(item => this._item = item),
		);

		const itemFull$ = state$.pipe(
			switchMap(state => this.workflowRestService.getWorkflowItemData$(state.projectId, state.participantId, state.itemId)),
			map(workflowItemDataFull => {
				const completed = !!workflowItemDataFull[0]?.completed;
				const itemFull = new SessionItemData(workflowItemDataFull[0]?.data);
				itemFull.setProperty('completed', completed);
				return itemFull;
			}),
			tap(itemFull => this._itemFull = itemFull),
		);

		this.subscription.add(item$.subscribe(this.item$));
		this.subscription.add(itemFull$.subscribe(this.itemFull$));

		this.subscription.add(this.item$.pipe(
			filter(item => !!item),
			switchMap(item => item.data$.pipe(
				skip(1), // ignore initial state
			)),
			tap(() => this._status.next(CommunicationStatus.PENDING)),
		).subscribe(data => {
			if (data) {
				this.update$.emit(data);
			}
			this._status.next(CommunicationStatus.DONE);
		}));

		this.subscription.add(this.item$.pipe(
			filter(item => !!item),
			switchMap(item => item.data$),
			take(1),
		).subscribe(() => this._status.next(CommunicationStatus.UNKNOWN)));

		this.subscription.add(this.itemFull$.pipe(
			filter(item => !!item),
			switchMap(item => item.data$),
			take(1),
		).subscribe(() => this._status.next(CommunicationStatus.UNKNOWN)));

		this.workflowStatus$ = combineLatest([state$, workflowService.workflowStatus$]).pipe(
			switchMap(([state, ]) => {
				return this.workflowRestService.getWorkflowStatus$(state.projectId, state.participantId).pipe(
					map(status => status.status[state.itemId] || { completed: false, started: false }),
					catchError(() => of({ completed: false, started: false })),
					switchMap(initialStatus => this.updateStatus$.pipe(
						startWith({}),
						scan((status, updateStatus) => ({...status, ...updateStatus}), initialStatus),
					)),
				);
			}),
			shareReplay({refCount: true, bufferSize: 1}),
		);


		this.current$ = this.workflowStatus$.pipe(
			switchMap(status => status?.completed ? this.itemFull$ : this.item$),
		);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	get communicationStatus$(): Observable<CommunicationStatus> {
		return this._status;
	}

	/**
	 * Get the current workflow items SessionItemData
	 */
	get item(): SessionItemData {
		return this._item;
	}

	set item(item: SessionItemData) {
		this._item = item;
		this.item$.next(item);
	}

	/**
	 * Get the current workflow items SessionItemData even after local inputs were deleted from completion
	 */
	get itemFull(): SessionItemData {
		return this._itemFull;
	}

	set itemFull(itemFull: SessionItemData) {
		this._itemFull = itemFull;
		this.itemFull$.next(itemFull);
	}

	/**
	 * Observable for a property on the current workflow items SessionItemData
	 *
	 * @param key property key
	 */
	property$(key: string | string[]): Observable<unknown> {
		return this.item$.pipe(
			switchMap(item => item ? item.property$(key) : EMPTY),
			distinctUntilChanged(isEqual),
		);
	}

	/**
	 * Observable for a property on the current workflow items SessionItemData even after local inputs were deleted from completion
	 *
	 * @param key property key
	 */
	propertyFull$(key: string | string[]): Observable<unknown> {
		return this.itemFull$.pipe(
			switchMap(item => item ? item.property$(key) : EMPTY),
			distinctUntilChanged(isEqual),
		);
	}

	/**
	 * Get a property on the current workflow items SessionItemData even after local inputs were deleted from completion
	 * @param key property key
	 */
	getPropertyFull(key: string | string[]): unknown {
		return this.itemFull.getProperty(key);
	}
	
	async finishWorkflow(): Promise<void> {
		await this.workflowService.finishWorkflow(this._participantId, this._itemId, this.item.data);
	}

	async doTransition(transition: CustomStateTransition): Promise<string> {
		return await this.workflowService.switchToState(transition, this._participantId, this._itemId);
	}
}
